/**
 * @-trigger suggestion for assigning project users in the description (rich text only).
 * Mirrors slash-menu "Assign user" behaviour; disabled while editing raw Markdown.
 */

import type { MutableRefObject } from 'react';
import { Extension } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion, { type SuggestionProps } from '@tiptap/suggestion';
import type { ShortcutConfig } from '../types';
import type { SlashMenuItem } from '../types';

export const AtMentionSuggestionPluginKey = new PluginKey('atMentionAssign');

export type AtMentionSuggestionOptions = {
  items: (props: { query: string; editor: import('@tiptap/core').Editor }) => SlashMenuItem[];
  render: () => {
    onStart: (props: SuggestionProps<SlashMenuItem, SlashMenuItem>) => void;
    onUpdate: (props: SuggestionProps<SlashMenuItem, SlashMenuItem>) => void;
    onKeyDown: (props: { event: KeyboardEvent }) => boolean;
    onExit: () => void;
  };
  /** When true, @ suggestions are disabled (e.g. raw Markdown source mode). Read live from a ref. */
  atMentionDisabled: () => boolean;
  shortcutConfigRef: MutableRefObject<ShortcutConfig | null>;
};

export const AtMentionSuggestion = Extension.create<AtMentionSuggestionOptions>({
  name: 'atMentionAssign',

  addOptions() {
    return {
      items: () => [],
      render: () => ({
        onStart: () => {},
        onUpdate: () => {},
        onKeyDown: () => false,
        onExit: () => {}
      }),
      atMentionDisabled: () => false,
      shortcutConfigRef: { current: null } as MutableRefObject<ShortcutConfig | null>
    };
  },

  addProseMirrorPlugins() {
    const extension = this;

    return [
      Suggestion({
        editor: extension.editor,
        char: '@',
        pluginKey: AtMentionSuggestionPluginKey,
        allowSpaces: false,
        startOfLine: false,
        allowedPrefixes: [' ', '\n'],
        command: ({ editor, range, props }) => {
          (props as SlashMenuItem).onSelect({ editor, range });
        },
        items: ({ query, editor }) => extension.options.items({ query, editor }),
        render: extension.options.render,
        allow: () => {
          if (extension.options.atMentionDisabled()) {
            return false;
          }
          const sc = extension.options.shortcutConfigRef.current;
          if (!sc?.canEditAssignees || !sc.assignableUsers?.length) {
            return false;
          }
          const usersToAssign = sc.assignableUsers.filter(
            u =>
              !sc.assignees.includes(u.id) &&
              (sc.currentUserId == null || String(u.id) !== String(sc.currentUserId))
          );
          return usersToAssign.length > 0;
        }
      })
    ];
  }
});
